/**
 * Agents feature — Zod schemas.
 *
 * The agent registry's response shapes are the contract's (api/openapi/agents.yaml), generated into
 * api.generated.ts by make docs; this module names them and holds the form, search and UI schemas.
 * Tenant-scoped surface: /api/v1/tenants/{tenant_id}/agents[/...]
 */

import {
  consequencePolicySchema,
  type ConsequencePolicy,
} from "@pureid/auth-core";
import { z } from "zod";

import {
  agentEnrollmentProvenanceSchema,
  agentRevokeResponseSchema,
  agentSchema,
  agentsListResponseSchema,
  budgetBreakdownSchema,
  budgetChainRollupRowSchema,
  budgetChainRollupSchema,
  budgetMemberSchema,
  budgetPoolSchema,
  ceilingChangeSchema,
  chainVerifySchema,
  cnfPinRequestSchema,
  createStandingDelegationResponseSchema,
  crewDrawSchema,
  crewHumanSchema,
  crewMemberSchema,
  crewPoolMemberSchema,
  crewPoolSchema,
  crewStepSchema,
  crewViewSchema,
  dimensionCatalogEntrySchema,
  dimensionCatalogResponseSchema,
  evidenceDecisionSchema,
  grantEvidenceSchema,
  grantLifecycleSchema,
  grantsListResponseSchema,
  killPrincipalResponseSchema,
  killSwitchResultSchema,
  pausePrincipalResponseSchema,
  recertifyAgentResponseSchema,
  resourceCustodySchema,
  resumePrincipalResponseSchema,
  simulateDimensionSchema,
  simulatePolicyResponseSchema,
  standingDelegationSchema,
  standingUtilizationSchema,
  stopPreviewSchema,
} from "./api.generated";

export {
  agentEnrollmentProvenanceSchema,
  agentRevokeResponseSchema,
  agentSchema,
  agentsListResponseSchema,
  budgetBreakdownSchema,
  budgetChainRollupRowSchema,
  budgetChainRollupSchema,
  budgetMemberSchema,
  budgetPoolSchema,
  ceilingChangeSchema,
  chainVerifySchema,
  cnfPinRequestSchema,
  createStandingDelegationResponseSchema,
  crewDrawSchema,
  crewHumanSchema,
  crewMemberSchema,
  crewPoolMemberSchema,
  crewPoolSchema,
  crewStepSchema,
  crewViewSchema,
  dimensionCatalogEntrySchema,
  dimensionCatalogResponseSchema,
  evidenceDecisionSchema,
  grantEvidenceSchema,
  grantLifecycleSchema,
  grantsListResponseSchema,
  killPrincipalResponseSchema,
  killSwitchResultSchema,
  pausePrincipalResponseSchema,
  recertifyAgentResponseSchema,
  resourceCustodySchema,
  resumePrincipalResponseSchema,
  simulateDimensionSchema,
  simulatePolicyResponseSchema,
  standingDelegationSchema,
  standingUtilizationSchema,
  stopPreviewSchema,
};

// Carries an Edge setup choice into new resource registration; it grants no authority.
export const protectedResourceSetupSearchSchema = z.object({
  setup: z.enum(["outbound"]).optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
  page: z.coerce.number().int().min(1).max(100000).optional().catch(undefined),
  protocol: z.enum(["https", "ssh", "sftp", "rdp"]).optional().catch(undefined),
});

// ── Enums (mirror the TEXT CHECK sets in db/migrations/000001_init.up.sql (was 85_agents.sql)) ──────────

export const agentLifecycleStateSchema = z.enum([
  "created",
  "certified",
  "active",
  "suspended",
  "quarantined",
  "retired",
]);
export type AgentLifecycleState = z.infer<typeof agentLifecycleStateSchema>;

// NHI principal-kind discriminator (Phase-1 SA vertical). One registry, three kinds.
export const agentKindSchema = z.enum(["agent", "workload_identity", "api_key"]);
export type AgentKind = z.infer<typeof agentKindSchema>;

export const agentRiskTierSchema = z.enum(["read_only", "low", "medium", "high"]);
export type AgentRiskTier = z.infer<typeof agentRiskTierSchema>;

export const agentAutonomyTierSchema = z.enum([
  "supervised",
  "bounded",
  "autonomous",
]);
export type AgentAutonomyTier = z.infer<typeof agentAutonomyTierSchema>;

export const agentDataClassSchema = z.enum([
  "public",
  "internal",
  "confidential",
  "restricted",
]);
export type AgentDataClass = z.infer<typeof agentDataClassSchema>;

export const agentAttestationSchema = z.enum([
  "token_only",
  "cloud_instance",
  "vtpm_measured",
]);
export type AgentAttestation = z.infer<typeof agentAttestationSchema>;

// ── List search (URL is the source of truth) ───────────────────────────────

export const agentsListSearchSchema = z.object({
  state: agentLifecycleStateSchema.optional(),
  kind: agentKindSchema.optional(),
  // Free-text name search (the Activity scope picker typeahead). Backed by a trigram-indexed ILIKE on
  // the server; below the 3-char trigram floor the server returns nothing.
  q: z.string().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
  // Create-intent from the "choose an identity type" guide: a deep-link that opens the matching
  // register flow on arrival (agent/workload_identity today; api_key when its create path ships). It is
  // a transient action, not filter state, so the list page consumes it once and strips it from the URL.
  register: agentKindSchema.optional(),
});
export type AgentsListSearch = z.infer<typeof agentsListSearchSchema>;

// ── Agent record — the contract's Agent (handler.go agentResponse) ─────────

export type Agent = z.infer<typeof agentSchema>;

// ── Dimension catalog — GET .../agents/consequence-dimensions ─────────────────
export type DimensionCatalogEntry = z.infer<typeof dimensionCatalogEntrySchema>;

export type DimensionCatalogResponse = z.infer<typeof dimensionCatalogResponseSchema>;

// ── Re-certification (tighten-only) — POST .../agents/{id}/recertify ──────────
// The request is the FULL proposed ceiling (a snapshot the operator edited, not a sparse patch).
// The SET + txn fields are omitted here: the dialog only re-edits the enum/depth/consequence
// dimensions, and an omitted set means "keep current" server-side (never narrow-to-empty). The
// server is authoritative and rejects any WIDEN (422 agent-recert-widens) or NO-OP (422 noop).
export const recertifyAgentSchema = z.object({
  risk_tier: agentRiskTierSchema,
  autonomy_tier: agentAutonomyTierSchema,
  allowed_data_class: agentDataClassSchema,
  min_attestation: agentAttestationSchema,
  max_delegation_depth: z.coerce.number().int().min(1).max(8),
  // Omit ⇒ the server KEEPS the current policy (nil = keep — service.go Recertify defaults the
  // proposed clamp to current). Sent as {thresholds:[]} when the editor is cleared, which the
  // server reads as the tightest (escalate-all) policy. Omission is also the fail-closed path
  // for policies carrying advanced rules the editor cannot rebuild without erasing them.
  consequence_policy: consequencePolicySchema.optional(),
  // Set + txn ceilings. The dialog sends these ONLY when the operator narrowed them (removed a
  // member / lowered the count); omitted ⇒ the server keeps the current value (never narrow-to-
  // empty). Tighten-only is server-enforced (a non-subset / higher count is a 422 widen).
  max_scope: z.array(z.string()).optional(),
  allowed_egress: z.array(z.string()).optional(),
  allowed_resources: z.array(z.string()).optional(),
  max_txn_count: z.coerce.number().int().positive().optional(),
});
export type RecertifyAgentInput = z.infer<typeof recertifyAgentSchema>;

// ── Policy simulator — POST .../agents/{id}/policy-simulate ───────────────────
// "What would this action do?" answered by the SAME effective-policy resolution +
// per-action gates the mint/PEP run (agent/simulate.go). Pure read; the optional
// draft_policy previews a proposed ceiling before saving.
export type SimulateDimension = z.infer<typeof simulateDimensionSchema>;

export type SimulatePolicyResponse = z.infer<typeof simulatePolicyResponseSchema>;

export interface SimulatePolicyInput {
  consequence: { measurements: Array<{ dimension: string; quantity: number }> };
  draft_policy?: ConsequencePolicy;
  // Hypothetical prior spend this window ("assume $X already spent") in smallest units — evaluated
  // against the cumulative caps so the budget-exhausted flip is previewable. Omit for a fresh window.
  assumed_spent?: { measurements: Array<{ dimension: string; quantity: number }> };
}

// ── Budget chain rollup — GET .../agents/{agent_id}/budget-chain-rollup ────────
export type BudgetChainRollupRow = z.infer<typeof budgetChainRollupRowSchema>;

export type BudgetChainRollup = z.infer<typeof budgetChainRollupSchema>;

// One narrowed dimension in the audit diff the server returns (agent.CeilingChange).
export type CeilingChange = z.infer<typeof ceilingChangeSchema>;

export type RecertifyAgentResponse = z.infer<typeof recertifyAgentResponseSchema>;

// Workload-identity registration (NHI). A workload_identity is a registry principal (same POST /agents
// endpoint, kind='workload_identity') but born DIFFERENTLY from an AI agent: it MUST have a federated
// workload identity (a trusted OIDC issuer + the workload's subject claim — an SA authenticates as a
// workload, not via an interactive passkey), a directory-user owner, and a mandatory backup owner
// (S2c). Autonomy is forced to 'bounded' server-side (non-agent principals never run supervised/
// unattended free-form). The form omits the agent preset/tier machinery — an SA is not scenario-authored.
export const createWorkloadIdentitySchema = z
  .object({
    name: z.string().min(1, "Name is required").max(128, "Name too long (max 128)"),
    purpose: z.string().min(1, "Purpose is required").max(1024, "Purpose too long (max 1024)"),
    owner_user_id: z.string().min(1, "Choose an owner"),
    backup_owner_user_id: z.string().min(1, "Choose a backup owner"),
    fed_issuer: z
      .string()
      .min(1, "Issuer is required")
      .url("Enter the issuer URL (e.g. https://oidc.myco.com)"),
    fed_subject: z.string().min(1, "Workload subject is required").max(256, "Subject too long"),
  })
  .superRefine((v, ctx) => {
    if (v.owner_user_id && v.backup_owner_user_id && v.owner_user_id === v.backup_owner_user_id) {
      ctx.addIssue({
        code: "custom",
        message: "The backup owner must differ from the owner.",
        path: ["backup_owner_user_id"],
      });
    }
  });
export type CreateWorkloadIdentityInput = z.infer<typeof createWorkloadIdentitySchema>;

// Owner reassignment (NHI S4c): re-anchor a principal to a new owner (+ a new backup owner for a
// workload identity) — the offboarding-recovery ceremony that also reactivates a suspended principal.
// The client sends directory-USER owners (the dominant NHI case: promote the backup user, name a fresh
// backup); the server enforces the full owner invariant (exactly one owner, SA needs a distinct backup)
// and re-validates tenant membership. principals.owner_reassign + step-up gated. Returns the updated
// agent (agentSchema). The SA-requires-backup + backup-distinct rules are applied in the dialog (which
// knows the principal kind) and re-enforced server-side.
export const reassignOwnerSchema = z.object({
  owner_user_id: z.string().min(1, "Choose a new owner"),
  backup_owner_user_id: z.string().optional(),
});
export type ReassignOwnerInput = z.infer<typeof reassignOwnerSchema>;

export type AgentRevokeResponse = z.infer<typeof agentRevokeResponseSchema>;

export type KillPrincipalResponse = z.infer<typeof killPrincipalResponseSchema>;

export type KillSwitchResult = z.infer<typeof killSwitchResultSchema>;

/** The key-pin body as the contract states it (PUT .../agents/{agent_id}/cnf-pin). */
export type CnfPinRequest = z.infer<typeof cnfPinRequestSchema>;

/**
 * The key-pin form. The thumbprint is the key's SHA-256 JWK thumbprint (RFC 7638) as unpadded
 * base64url: exactly 43 characters of A–Z, a–z, 0–9, "-" and "_". Surrounding whitespace is
 * trimmed; padding is refused rather than silently stripped, so what is pinned is what was typed.
 */
export const cnfPinFormSchema = z.object({
  cnf_method: cnfPinRequestSchema.shape.cnf_method,
  cnf_thumbprint: z
    .string()
    .trim()
    .min(1, "Enter the key's thumbprint.")
    .refine((v) => !v.endsWith("="), "Remove the trailing '=' padding; the thumbprint is unpadded base64url.")
    .refine((v) => /^[A-Za-z0-9_-]*$/.test(v), "Use base64url characters only: letters, digits, '-' and '_'.")
    .refine((v) => v.length === 43, "A SHA-256 thumbprint is exactly 43 characters of unpadded base64url."),
});
export type CnfPinForm = z.infer<typeof cnfPinFormSchema>;

export type StopPreview = z.infer<typeof stopPreviewSchema>;

export type PausePrincipalResponse = z.infer<typeof pausePrincipalResponseSchema>;

export type ResumePrincipalResponse = z.infer<typeof resumePrincipalResponseSchema>;

// ── Grant evidence (the signed, hash-chained decision trail for one grant) ──────
export type EvidenceDecision = z.infer<typeof evidenceDecisionSchema>;

export type GrantEvidence = z.infer<typeof grantEvidenceSchema>;

export type ChainVerifyResult = z.infer<typeof chainVerifySchema>;

// ── Grant lifecycle (the live list of agent grants + effective status) ──────────
export type GrantLifecycle = z.infer<typeof grantLifecycleSchema>;

export type GrantsListResponse = z.infer<typeof grantsListResponseSchema>;

// ── Protected resource (the DB registry) ──
// The list endpoint is the source of truth for registered resources; `registry_status` is the
// lifecycle (active|disabled|deleted).
export const resourceDirectionSchema = z.enum(["ingress", "egress"]);
export type ResourceDirection = z.infer<typeof resourceDirectionSchema>;

export const resourceDataClassSchema = z.enum(["public", "internal", "confidential", "restricted"]);
export type ResourceDataClass = z.infer<typeof resourceDataClassSchema>;

export const resourceRegistryStatusSchema = z.enum(["active", "disabled", "deleted"]);
export type ResourceRegistryStatus = z.infer<typeof resourceRegistryStatusSchema>;

export const protectedResourceSchema = z.object({
  // Registry fields (config).
  id: z.string(),
  tenant_id: z.string(),
  resource: z.string(),
  audience: z.string(),
  upstream_url: z.string(),
  data_class: z.string(),
  direction: z.string(),
  egress: z.string().optional().default(""),
  server_cert_sha256: z.array(z.string()).optional().default([]),
  ca_cert_pem: z.string().optional().default(""),
  application_id: z.string().optional().default(""),
  /** Whether people may reach this application through the PRIVATE door — a passkey
   *  ceremony and a session — as distinct from agents calling it per call on the API
   *  door. Defaults false: a control plane that does not send the key has not opted
   *  anything in. */
  private_access: z.boolean().optional().default(false),
  /** The application's own name under the private door. The label is the operator's; the
   *  hostname (<label>.<tenant>.<application domain>) is derived by the control plane and is what a
   *  person's browser reaches the application at. Present exactly when private_access is set. */
  private_label: z.string().optional().default(""),
  hostname: z.string().optional().default(""),
  /** Whether a session on this application needs an unexpired device-posture verdict from the
   *  customer's posture provider (posture_unknown without one). A deny verdict ends sessions on
   *  every application, required or not. Only meaningful with private_access. */
  require_posture: z.boolean().optional().default(false),
  /** What the resource speaks (private-access ruling R10). "https" is forwarded to the upstream;
   *  "ssh" is a server a person reaches in a terminal rendered at the edge, with the target's
   *  host key pinned and one login presented; "rdp" is a Windows computer they reach as a screen,
   *  with the server's own certificate pinned and one account signed in. The authority an SSH
   *  target trusts is your own: it is read on the Edge's host and named on the server
   *  roster, never served from here. */
  protocol: z.enum(["https", "ssh", "sftp", "rdp"]).optional().default("https"),
  native_ssh_enabled: z.boolean().optional(),
  require_connection_approval: z.boolean().optional(),
  ssh_auth_method: z.enum(["certificate", "password", "keyboard_interactive"]).optional().default("certificate"),
  ssh_host_key: z.string().optional().default(""),
  ssh_login: z.string().optional().default(""),
  rdp_login: z.string().optional().default(""),
  rdp_domain: z.string().optional().default(""),
  /** The one line this server's AuthorizedPrincipalsFile lists for the login. */
  ssh_authorized_principal: z.string().optional().default(""),
  /** How the door introduces the person to this application (private-access ruling R9): the
   *  header it sets on every request and where the keys that sign it are published. Present
   *  exactly when the resource has a hostname. */
  assertion_header: z.string().optional().default(""),
  assertion_jwks_url: z.string().optional().default(""),
  registry_status: z.string(),
  created_by: z.string().optional().default(""),
  updated_by: z.string().optional().default(""),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProtectedResource = z.infer<typeof protectedResourceSchema>;

export type ResourceCustody = z.infer<typeof resourceCustodySchema>;

export const protectedResourceListSchema = z.object({
  resources: z.array(protectedResourceSchema),
  total: z.number(),
  page: z.number().int().positive().optional(),
  page_size: z.number().int().positive().max(200).optional(),
});
export type ProtectedResourceList = z.infer<typeof protectedResourceListSchema>;

// 64-char lowercase hex SHA-256 server-cert pin (mirrors the backend validator).
const sha256HexSchema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, "must be a 64-char lowercase hex SHA-256");

// CREATE input — field-format validation only. resource/audience/direction are immutable
// post-create (server-enforced). The CROSS-FIELD trust rules (anchor required; egress needs
// an identity, ingress must not) live in validateResourceTrust() so create AND edit share one
// implementation (the edit payload omits the immutable direction). See that helper.
export const resourceProtocolSchema = z.enum(["https", "ssh", "sftp", "rdp"]);
export type ResourceProtocol = z.infer<typeof resourceProtocolSchema>;

// The upstream's shape follows the protocol: https is an absolute https URL, ssh is ssh://host:port
// and nothing more (no path, no user). Mirrors agentresource.validateUpstreamURL.
const upstreamURLSchema = z.string().min(1, "Upstream URL is required");
const sshHostKeyLine = /^(ssh-ed25519|ecdsa-sha2-nistp256|ecdsa-sha2-nistp384|ecdsa-sha2-nistp521|ssh-rsa) [A-Za-z0-9+/]+=*( .*)?$/;
const sshLoginRe = /^[a-z_][a-z0-9_.-]{0,31}$/;
// A Windows account name, without the characters Windows itself forbids in one. A backslash is
// refused because DOMAIN\login is two fields here. Mirrors agentresource.rdpLoginRe.
const rdpLoginRe = /^[^\\/[\]:;|=,+*?<>"\s]{1,104}$/;
const rdpDomainRe = /^[A-Za-z0-9]([A-Za-z0-9.-]{0,62}[A-Za-z0-9])?$/;
// Control characters are excluded as a separate test rather than as a range in the pattern above,
// which a regular expression cannot carry without becoming unreadable to the next person.
function printable(value: string): boolean {
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return false;
  }
  return true;
}

export const createProtectedResourceSchema = z.object({
  resource: z
    .string()
    .min(1, "Resource name is required")
    .regex(/^[^\s/]+$/, "No spaces or slashes (a single path segment)"),
  audience: z.string().min(1, "Audience is required"),
  upstream_url: upstreamURLSchema,
  direction: resourceDirectionSchema,
  data_class: resourceDataClassSchema,
  egress: z.string().optional(),
  server_cert_sha256: z.array(sha256HexSchema),
  ca_cert_pem: z.string().optional(),
  application_id: z.string().optional(),
  private_access: z.boolean().optional(),
  private_label: z.string().optional(),
  require_posture: z.boolean().optional(),
  protocol: resourceProtocolSchema.optional(),
  native_ssh_enabled: z.boolean().optional(),
  require_connection_approval: z.boolean().optional(),
  ssh_auth_method: z.enum(["certificate", "password", "keyboard_interactive"]).optional(),
  ssh_host_key: z.string().optional(),
  ssh_login: z.string().optional(),
  rdp_login: z.string().optional(),
  rdp_domain: z.string().optional(),
});
export type CreateProtectedResourceInput = z.infer<typeof createProtectedResourceSchema>;

// UPDATE input — trust/routing fields only (resource/audience/direction are immutable).
export const updateProtectedResourceSchema = z.object({
  upstream_url: upstreamURLSchema,
  data_class: resourceDataClassSchema,
  egress: z.string().optional(),
  server_cert_sha256: z.array(sha256HexSchema),
  ca_cert_pem: z.string().optional(),
  application_id: z.string().optional(),
  private_access: z.boolean().optional(),
  private_label: z.string().optional(),
  require_posture: z.boolean().optional(),
  native_ssh_enabled: z.boolean().optional(),
  require_connection_approval: z.boolean().optional(),
  ssh_auth_method: z.enum(["certificate", "password", "keyboard_interactive"]).optional(),
  ssh_host_key: z.string().optional(),
  ssh_login: z.string().optional(),
  rdp_login: z.string().optional(),
  rdp_domain: z.string().optional(),
});
export type UpdateProtectedResourceInput = z.infer<typeof updateProtectedResourceSchema>;

// validateResourceTrust enforces the cross-field trust rules shared by create + edit, keyed
// on the resource's direction (which is immutable, so the edit form passes the existing one):
//   • a server trust anchor (a pin OR a CA PEM) is required for BOTH directions — ingress per
//     the server validator, egress per the edge build (it refuses an unauthenticated third
//     party). Requiring it client-side prevents registering something the edge can't serve.
//   • egress destinations MUST carry an identity; ingress MUST NOT (server requires egress="").
// Returns a field→message map (empty = valid). The server remains authoritative.
export function validateResourceTrust(input: {
  direction: ResourceDirection;
  egress?: string;
  server_cert_sha256: string[];
  ca_cert_pem?: string;
  private_access?: boolean;
  private_label?: string;
  protocol?: ResourceProtocol;
  upstream_url?: string;
  ssh_host_key?: string;
  ssh_login?: string;
  native_ssh_enabled?: boolean;
  require_connection_approval?: boolean;
  ssh_auth_method?: "certificate" | "password" | "keyboard_interactive";
  rdp_login?: string;
  rdp_domain?: string;
}): Record<string, string> {
  const errs: Record<string, string> = {};
  const protocol = input.protocol ?? "https";
  if (input.require_connection_approval && (!input.private_access || input.direction !== "ingress" || !["ssh", "sftp", "rdp"].includes(protocol))) {
    errs.require_connection_approval = "Connection approval requires a private server resource.";
  }
  if (input.native_ssh_enabled && (protocol !== "ssh" || (input.ssh_auth_method ?? "certificate") !== "certificate")) {
    errs.native_ssh_enabled = "Native SSH requires certificate authentication.";
  }
  const upstream = (input.upstream_url ?? "").trim();
  // AN SSH RESOURCE IS COMPLETE OR REFUSED (private-access ruling R10): private access, an
  // ssh://host:port upstream, the target's host key pinned, one login — and no TLS trust anchor,
  // which means nothing to it. Mirrors agentresource.validateProtocol; the server is authoritative.
  if (protocol === "ssh" || protocol === "sftp") {
    if (protocol === "sftp" && (input.ssh_auth_method ?? "certificate") !== "certificate") {
      errs.ssh_auth_method = "SFTP requires a temporary SSH certificate.";
    }
    if (!/^(ssh|sftp):\/\/[^/\s@]+:[0-9]{1,5}$/.test(upstream) || !upstream.startsWith(`${protocol}://`)) {
      errs.upstream_url = `The target is ${protocol}://host:port, without a path or user.`;
    }
    if (input.direction !== "ingress" || !input.private_access) {
      errs.private_access = "SSH is reached by a person in a rendered terminal: it is an ingress resource with private access on.";
    }
    if (!sshHostKeyLine.test((input.ssh_host_key ?? "").trim())) {
      errs.ssh_host_key = "Paste the target's host key as one authorized-keys line (ssh-ed25519, ecdsa-sha2-nistp*, or ssh-rsa).";
    }
    if (!sshLoginRe.test((input.ssh_login ?? "").trim())) {
      errs.ssh_login = "The login is one POSIX user name: lowercase, up to 32 characters.";
    }
    if (input.server_cert_sha256.length > 0 || (input.ca_cert_pem?.trim() ?? "") !== "") {
      errs.server_cert_sha256 = "An SSH target is pinned by its host key, not a TLS certificate.";
    }
    if (input.egress && input.egress.trim() !== "") {
      errs.egress = "Ingress resources must not set an egress identity.";
    }
    if ((input.rdp_login ?? "").trim() !== "" || (input.rdp_domain ?? "").trim() !== "") {
      errs.rdp_login = "A Windows account belongs to an RDP resource.";
    }
    return errs;
  }
  // AN RDP RESOURCE IS COMPLETE OR REFUSED, on the same terms: private access, an rdp://host:port
  // upstream, the server's own certificate pinned — exactly one, because a session must be able to
  // say which server it reached — and the Windows account it signs in as.
  if (protocol === "rdp") {
    if (!/^rdp:\/\/[^/\s@]+:[0-9]{1,5}$/.test(upstream)) {
      errs.upstream_url = "A Windows target is rdp://host:port — no path, no user.";
    }
    if (input.direction !== "ingress" || !input.private_access) {
      errs.private_access = "RDP is reached by a person on a rendered screen: it is an ingress resource with private access on.";
    }
    if (input.server_cert_sha256.length !== 1) {
      errs.server_cert_sha256 = "Pin the server's own certificate — exactly one, which is what the session compares before it sends the account's password.";
    }
    if ((input.ca_cert_pem?.trim() ?? "") !== "") {
      errs.ca_cert_pem = "A Windows target is pinned by that one certificate, not by a CA.";
    }
    const account = (input.rdp_login ?? "").trim();
    if (!rdpLoginRe.test(account) || !printable(account)) {
      errs.rdp_login = "The Windows account this session signs in as. Put the domain in its own field rather than writing DOMAIN\\account.";
    }
    const domain = (input.rdp_domain ?? "").trim();
    if (domain !== "" && !rdpDomainRe.test(domain)) {
      errs.rdp_domain = "A Windows domain name, or leave it empty for a local account.";
    }
    if ((input.ssh_host_key ?? "").trim() !== "" || (input.ssh_login ?? "").trim() !== "") {
      errs.ssh_host_key = "A host key and a login belong to an SSH resource.";
    }
    if (input.egress && input.egress.trim() !== "") {
      errs.egress = "Ingress resources must not set an egress identity.";
    }
    return errs;
  }
  if (upstream !== "" && !/^https:\/\//.test(upstream)) {
    errs.upstream_url = "Must be an absolute https URL";
  } else if (upstream !== "") {
    try {
      new URL(upstream);
    } catch {
      errs.upstream_url = "Must be an absolute https URL";
    }
  }
  if ((input.ssh_host_key ?? "").trim() !== "" || (input.ssh_login ?? "").trim() !== "") {
    errs.ssh_host_key = "A host key and a login belong to an SSH resource.";
  }
  if ((input.rdp_login ?? "").trim() !== "" || (input.rdp_domain ?? "").trim() !== "") {
    errs.rdp_login = "A Windows account belongs to an RDP resource.";
  }
  // Required for ingress only, matching agentresource.ValidateCreate exactly. Ingress is a private
  // host we own, so no public CA vouches for it and a pin (or explicit CA) is the only thing
  // between us and an arbitrary internal address. Egress is a third party on the public internet,
  // where the web PKI is the anchor and pinning is opt-in hardening.
  const hasAnchor = input.server_cert_sha256.length > 0 || (input.ca_cert_pem?.trim() ?? "") !== "";
  if (input.direction === "ingress" && !hasAnchor) {
    errs.server_cert_sha256 = "A server trust anchor is required for a resource you own — add a pin or a CA PEM.";
  }
  if (input.direction === "egress") {
    if (!input.egress || input.egress.trim() === "") {
      errs.egress = "An egress destination identity is required for direction = egress.";
    }
  } else if (input.egress && input.egress.trim() !== "") {
    errs.egress = "Ingress resources must not set an egress identity.";
  }
  // The private door admits a PERSON to an internal application; egress names a third party we
  // govern agents reaching. Opposite directions of travel, and the edge presents no client
  // certificate on an egress leg — so a private session forwarded there would arrive at a third
  // party carrying a person's identity and none of the mutual authentication the private door
  // requires. Mirrors agentresource.validatePrivateAccess; the server remains authoritative.
  if (input.private_access && input.direction !== "ingress") {
    errs.private_access =
      "Private access is for resources you own. An egress destination is a third party your agents reach, not an application a person opens.";
  }
  // The label becomes the application's hostname under the private door, so it is required
  // whenever the door is opened and must be one DNS label. Mirrors agentresource.validatePrivateLabel.
  if (input.private_access) {
    const label = (input.private_label ?? "").trim();
    if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(label)) {
      errs.private_label =
        "Choose the application's name under the private door: lowercase letters, digits and hyphens, up to 63 characters.";
    }
  }
  return errs;
}

export type AgentsListResponse = z.infer<typeof agentsListResponseSchema>;

// Lineage schema (mirrors handler.go LineageGraph) is deferred to Phase 2 along
// with the sub-agent write path — the read endpoint is always empty until then.

// ── Create / update inputs ─────────────────────────────────────────────────

export const createAgentSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(128, "Name too long (max 128)"),
    purpose: z
      .string()
      .min(1, "Purpose is required")
      .max(1024, "Purpose too long (max 1024)"),
    // Exactly one owner. The form supplies one of these; the backend also
    // enforces the invariant.
    owner_admin_id: z.string().optional(),
    owner_user_id: z.string().optional(),
    risk_tier: agentRiskTierSchema.default("read_only"),
    autonomy_tier: agentAutonomyTierSchema.default("supervised"),
    max_delegation_depth: z.coerce.number().int().min(1).max(8).default(1),
    allowed_data_class: agentDataClassSchema.default("public"),
    min_attestation: agentAttestationSchema.default("cloud_instance"),
    // Bearer opt-in: may this identity be presented by a PASTED key rather than
    // only by proof of possession. Deny-by-default, and create-only by
    // construction — it is a mint ceiling, and ceilings freeze once the agent
    // leaves `created` (recertify is tighten-only, so switching it on later is a
    // widening the model refuses). Always sent with min_attestation:"token_only";
    // see proofModeToWire, which is the only thing that should produce this pair.
    bearer_presentation: z.boolean().default(false),
    max_scope: z.array(z.string()).default([]),
    // What this identity may REACH. Deny-all by default, and tighten-only at recertify — so a
    // value set here can be narrowed later but never widened, which is why the create dialog
    // requires one on the pasted-key path rather than leaving it for a later screen.
    //
    // It was absent from this schema while the dialog was being taught to send it, which Zod
    // would have handled by silently stripping the key — the field would simply never have
    // arrived, with nothing failing anywhere to say so.
    allowed_resources: z.array(z.string()).default([]),
    allowed_egress: z.array(z.string()).default([]),
    // The certified consequence policy (generic per-dimension thresholds). Optional: omit ⇒
    // every value-bearing call escalates. Settable at create (and while `created`) only — it
    // is a capability ceiling frozen once the agent advances.
    consequence_policy: consequencePolicySchema.optional(),
  })
  .superRefine((value, ctx) => {
    // Owner is optional from the client: the backend defaults it to the
    // authenticated admin. Only reject the contradictory case of naming both.
    if (value.owner_admin_id && value.owner_user_id) {
      ctx.addIssue({
        code: "custom",
        message: "Provide at most one owner — an admin or a user, not both.",
        path: ["owner_admin_id"],
      });
    }
  });
export type CreateAgentInput = z.infer<typeof createAgentSchema>;

export const updateAgentSchema = z.object({
  purpose: z.string().min(1).max(1024).optional(),
  risk_tier: agentRiskTierSchema.optional(),
  autonomy_tier: agentAutonomyTierSchema.optional(),
  max_delegation_depth: z.coerce.number().int().min(1).max(8).optional(),
  allowed_data_class: agentDataClassSchema.optional(),
  min_attestation: agentAttestationSchema.optional(),
  max_scope: z.array(z.string()).optional(),
  allowed_egress: z.array(z.string()).optional(),
  // The certified consequence policy (generic per-dimension thresholds). A capability ceiling,
  // so the backend only honors a change while the agent is `created` (touchesCeiling). Omit to
  // leave unchanged.
  consequence_policy: consequencePolicySchema.optional(),
  // The portal-delegation allowlist. NOT a capability ceiling (service.go
  // touchesCeiling excludes it), so it is editable in ANY lifecycle state — an
  // operator may grant/revoke delegators on an active agent without re-certification.
  delegable_by_user_ids: z.array(z.string()).optional(),
});
export type UpdateAgentInput = z.infer<typeof updateAgentSchema>;

// ── Standing delegations (P6e) ───────────────────────────────────────────────
// The durable, config-time human pre-authorization an unattended autonomous agent
// mints short-lived grants against (no per-run nonce). Wire contract:
//   /api/v1/tenants/{tenant_id}/agents/standing-delegations[/{id}]

// The effective status as the contract states it: a lapsed 'active' row is surfaced as 'expired'.
export const standingDelegationStatusSchema = standingDelegationSchema.shape.status;
export type StandingDelegationStatus = z.infer<typeof standingDelegationStatusSchema>;

// How the human authority behind a standing delegation was established — an honest provenance label,
// not a security control. `admin_console` = an operator provisioned it from the admin console;
// `short_term` = it was captured from a short-term (start-by/active-window) assurance approval;
// `agent_requested` = the user approved the agent's own request on their passkey.
export type ConsentOrigin = NonNullable<StandingDelegation["consent_origin"]>;

export type StandingUtilization = z.infer<typeof standingUtilizationSchema>;

export type BudgetMember = z.infer<typeof budgetMemberSchema>;

export type BudgetPool = z.infer<typeof budgetPoolSchema>;

export type BudgetBreakdown = z.infer<typeof budgetBreakdownSchema>;

export type CrewMember = z.infer<typeof crewMemberSchema>;


export type CrewStep = z.infer<typeof crewStepSchema>;

export type CrewPool = z.infer<typeof crewPoolSchema>;


export type CrewView = z.infer<typeof crewViewSchema>;

export type StandingDelegation = z.infer<typeof standingDelegationSchema>;

// The list endpoint returns a BARE ARRAY (no pagination wrapper) — the set is small.
export const standingDelegationsListSchema = z.array(standingDelegationSchema);
export type StandingDelegationsList = z.infer<typeof standingDelegationsListSchema>;

export type CreateStandingDelegationResponse = z.infer<
  typeof createStandingDelegationResponseSchema
>;

// Create INPUT (wire body). No .default() here — the dialog supplies defaults in its state.
export const createStandingDelegationSchema = z.object({
  agent_id: z.string().min(1, "An agent is required"),
  // The APPROVER (delegating end-user) on whose behalf the standing delegation is created — the
  // human whose passkey approves the agent's over-threshold (T3) escalations. Omitted ⇒ the
  // creating admin is the approver (an explicit "I'll approve" self-delegation, never the silent
  // default — the dialog forces an explicit choice). When set, the backend allowlist-gates it.
  delegating_user_id: z.string().uuid().optional(),
  resource: z.string().min(1, "A resource is required"),
  audience: z.string().min(1, "An audience is required"),
  scope: z.array(z.string().min(1)).min(1, "At least one scope is required"),
  egress: z.array(z.string()),
  data_class: agentDataClassSchema,
  purpose: z.string().min(1, "A purpose is required").max(1024),
  // The frozen autonomous-headroom policy (generic per-dimension thresholds). Omit ⇒ every
  // value-bearing call escalates to the approver's passkey (deny-by-default).
  frozen_consequence_policy: consequencePolicySchema.optional(),
  max_uses: z.coerce.number().int().positive().optional(),
  valid_for_seconds: z.coerce.number().int().positive(),
});
export type CreateStandingDelegationInput = z.infer<typeof createStandingDelegationSchema>;

// URL-state for the list — a client-side status filter (the backend returns all statuses).
export const standingDelegationsSearchSchema = z.object({
  status: standingDelegationStatusSchema.optional(),
});
export type StandingDelegationsSearch = z.infer<typeof standingDelegationsSearchSchema>;

export type AgentEnrollmentProvenance = z.infer<typeof agentEnrollmentProvenanceSchema>;

// ── Operations on a protected resource ─────────────────────────────────────────────────────
//
// An operation classifies one request shape on a resource: its matcher (method + path), whether
// it is reversible (an irreversible operation needs a person on every call), which body field
// names the counterparty, the data-class floor, and the MAGNITUDE FIELDS — body field → the
// consequence dimension a human's approval is measured in. The egress door binds a T3 approval's
// magnitudes with this map; an operation without one binds only its named parameters.
export const resourceOperationSchema = z.object({
  id: z.string(),
  resource_id: z.string(),
  operation: z.string(),
  reversible: z.boolean(),
  counterparty_param: z.string().optional().default(""),
  data_class_floor: z.string().optional().default(""),
  http_method: z.string(),
  path_pattern: z.string(),
  magnitude_fields: z.record(z.string(), z.string()).optional().default({}),
});
export type ResourceOperation = z.infer<typeof resourceOperationSchema>;

export const resourceOperationListSchema = z.object({
  operations: z.array(resourceOperationSchema),
});

export const OPERATION_KEY_RE = /^[a-z][a-z0-9_]{0,63}$/;
export const OPERATION_HTTP_METHODS = ["*", "GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"] as const;

export interface RegisterOperationInput {
  operation: string;
  reversible: boolean;
  counterparty_param?: string;
  data_class_floor?: string;
  http_method: string;
  path_pattern: string;
  magnitude_fields: Record<string, string>;
}

/** A PATCH: every field optional; absent means unchanged; magnitude_fields replaces the map. */
export interface UpdateOperationInput {
  reversible?: boolean;
  counterparty_param?: string;
  data_class_floor?: string;
  http_method?: string;
  path_pattern?: string;
  magnitude_fields?: Record<string, string>;
}

export function validateOperationForm(input: {
  operation: string;
  path_pattern: string;
  counterparty_param: string;
  magnitude_fields: Record<string, string>;
}): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!OPERATION_KEY_RE.test(input.operation)) {
    errs.operation = "Lowercase letters, digits and underscores; starts with a letter; up to 64 characters.";
  }
  if (!input.path_pattern.startsWith("/") || input.path_pattern.length > 512) {
    errs.path_pattern = "Starts with / and is at most 512 characters.";
  }
  if (input.counterparty_param && !OPERATION_KEY_RE.test(input.counterparty_param)) {
    errs.counterparty_param = "A body field name: lowercase letters, digits and underscores.";
  }
  const entries = Object.entries(input.magnitude_fields);
  if (entries.length > 8) {
    errs.magnitude_fields = "At most eight magnitude fields.";
  }
  for (const [field, dim] of entries) {
    if (!OPERATION_KEY_RE.test(field) || !OPERATION_KEY_RE.test(dim)) {
      errs.magnitude_fields = "Each magnitude field and its dimension are lowercase identifiers.";
      break;
    }
  }
  return errs;
}
